import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '../types';
import apiService from '../services/api.service';
import { STORAGE_KEYS } from '../config/api';

// Features disponibles selon le plan de l'organisation
export interface OrganizationFeatures {
  private_events: boolean;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  features: OrganizationFeatures | null;
  plan: string | null;
  // null tant qu'on ne sait pas encore : la bannière de collecte ne doit pas
  // clignoter au chargement en supposant que le régime manque.
  vatRegimeDeclared: boolean | null;
  // Mandat de facturation : l'organisation doit avoir accepté la version
  // COURANTE des CGU. null tant qu'on ne sait pas — le portail ne doit pas
  // s'ouvrir sur une supposition, ni se fermer sur une autre.
  termsAcceptanceRequired: boolean | null;
  termsCurrentVersion: string | null;
  acceptTerms: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  refreshVatRegime: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [features, setFeatures] = useState<OrganizationFeatures | null>(null);
  const [plan, setPlan] = useState<string | null>(null);
  const [vatRegimeDeclared, setVatRegimeDeclared] = useState<boolean | null>(null);
  const [termsAcceptanceRequired, setTermsAcceptanceRequired] = useState<boolean | null>(null);
  const [termsCurrentVersion, setTermsCurrentVersion] = useState<string | null>(null);

  // Charger les features de l'organisation après authentification
  const loadFeatures = async () => {
    try {
      const response = await apiService.getOrganizationFeatures();
      setFeatures(response.features);
      setPlan(response.plan);
      setVatRegimeDeclared(response.vat_regime_declared ?? null);
      setTermsAcceptanceRequired(
        typeof response.terms_acceptance_required === 'boolean' ? response.terms_acceptance_required : null
      );
      setTermsCurrentVersion(response.terms_current_version ?? null);
    } catch (error) {
      console.error('Failed to load organization features:', error);
      // En cas d'erreur, utiliser les valeurs par défaut (basic plan)
      setFeatures({
        private_events: false,
      });
      setPlan('basic');
      // On ne sait pas : rester à null plutôt que d'afficher une bannière
      // réclamant un régime peut-être déjà déclaré.
      setVatRegimeDeclared(null);
      setTermsAcceptanceRequired(null);
      setTermsCurrentVersion(null);
    }
  };

  // Enregistre l'acceptation côté serveur (horodatée, versionnée, signée du
  // compte), puis relit l'état : le portail se ferme sur ce que dit l'API,
  // pas sur un état local optimiste.
  const acceptTerms = async () => {
    await apiService.acceptOrganizationTerms();
    await loadFeatures();
  };

  // Appelé après la déclaration du régime pour retirer la bannière sans
  // recharger la page.
  const refreshVatRegime = async () => {
    await loadFeatures();
  };

  const checkAuth = async () => {
    setIsLoading(true);
    try {
      // Les cookies HTTP-only sont envoyés automatiquement
      // Plus besoin de gérer les tokens manuellement
      console.log('📡 Validation de la session avec /auth/me...');
      const response = await apiService.validateToken();
      console.log('✅ Session validée, user:', response.data);

      if (response.data) {
        setUser(response.data);
        // Stocker uniquement les données utilisateur pour l'hydratation UI
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(response.data));
        // Charger les features de l'organisation
        await loadFeatures();
      } else {
        console.log('❌ Réponse vide de /auth/me');
        setUser(null);
        setFeatures(null);
        setPlan(null);
        apiService.clearAuth();
      }
    } catch (error: any) {
      // 401 = pas de session valide (normal si pas connecté)
      if (error.response?.status === 401) {
        console.log('🔒 Pas de session active');
      } else {
        console.error('❌ Auth check failed:', error);
      }
      setUser(null);
      setFeatures(null);
      setPlan(null);
      apiService.clearAuth();
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      const response = await apiService.login(email, password);

      if (response.user) {
        setUser(response.user);
        // Charger les features après login
        await loadFeatures();
      }
    } catch (error: any) {
      console.error('Login failed:', error);
      throw new Error(error.response?.data?.detail || 'Échec de la connexion');
    }
  };

  const logout = async () => {
    await apiService.logout();
    setUser(null);
    setFeatures(null);
    setPlan(null);
    setVatRegimeDeclared(null);
    setTermsAcceptanceRequired(null);
    setTermsCurrentVersion(null);
    window.location.href = '/';
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const value = {
    user,
    isLoading,
    isAuthenticated: !!user,
    features,
    plan,
    vatRegimeDeclared,
    termsAcceptanceRequired,
    termsCurrentVersion,
    acceptTerms,
    login,
    logout,
    checkAuth,
    refreshVatRegime,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};